import React, { useEffect, useState, useRef } from "react";

import Box from "@mui/material/Box";
import Cropper from "react-easy-crop";
import Stack from "@mui/material/Stack";
import SunEditor from "suneditor-react";
import Slider from "@mui/material/Slider";
import 'suneditor/dist/css/suneditor.min.css';
import { useLocation } from "react-router-dom";
import Typography from "@mui/material/Typography";
import { useDispatch, useSelector } from "react-redux";

import { S3_BUCKET } from "../../../constants";
import { ChooseImage } from "../../../components/modals";
import LoaderGif from "../../../assets/images/loader.gif";
import { fundRaiserPageAction } from "../../../store/actions";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { rollBackToDefaultCoverOnError } from "../../../helpers";
import { SquareStackIcon, UploadIcon } from "../../../components/Icons";
import { useNonInitialEffect } from "../../../hooks/UseNonInitialEffect";
import { getCroppedImg, resizeLargerFile } from "../../../helpers/CropImage";
import { Dropdown, MixPanelTrackedLink, UploadImage } from "../../../components";

export default function ({ state, setState, handleChange, editView, validationErrors }) {
    const location = useLocation();
    const preSelectedCampaign = location.state?.campaignId || 0;
    

    const dispatch = useDispatch();
    const imageUploadRef = useRef();
    const missionStatementRef = useRef();

    const [zoom, setZoom] = useState(1);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [openDefaultImagesModal, setOpenDefaultImagesModal] = useState(false);
    const [missionStatement, setMissionStatement] = useState({ ...state.missionStatement });

    const campaigns = useSelector(state => state.campaigns.campaigns);
    const occasions = useSelector(state => state.occasions.occasions);
    const defaultImages = useSelector(state => state.fundraiser_page.default_images);

    const uploadImage = async (event) => {
        if (event.target.files[0]) {
            let fileReader = new FileReader();
            let newImage = event.target.files[0];
            let sizeInMB = (event.target.files[0].size / (1024 * 1024)).toFixed(2);

            dispatch(fundRaiserPageAction.updateLoaderState(true));

            if (sizeInMB > 3) newImage = await resizeLargerFile(newImage)
            fileReader.readAsDataURL(newImage);

            fileReader.onloadend = () => {
                dispatch(fundRaiserPageAction.updateLoaderState(false));
                handleChange({ target: { name: "selectedImage", value: { ...state.selectedImage, path: fileReader.result, default: false } } })
            }
        }
    }

    const onCropComplete = async (croppedArea, croppedAreaPixels) => {
        if (state.selectedImage.path) {
            try {
                const canvas = await getCroppedImg(
                    state.selectedImage.path,
                    croppedAreaPixels
                );
                const reader = new FileReader()
                canvas.toBlob(blob => {
                    reader.readAsDataURL(blob)
                    reader.onloadend = () => {
                        dataURLtoFile(reader.result, 'cropped.jpg')
                    }
                })
            }
            catch (e) {
                console.error(e)
            }
        }

    }

    const dataURLtoFile = (dataurl, filename) => {
        let arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        let croppedImage = new File([u8arr], filename, { type: mime });
        handleChange({ target: { name: "selectedImage", value: { ...state.selectedImage, file: croppedImage, default: false } } })

    }

    const onEditorChange = (content) => {
        if (missionStatementRef.current) {
            missionStatementRef.current.value = content

            setMissionStatement({
                story: missionStatementRef.current.editor.getText(), storyWithTags: content
            });
        }
    }

    const mapCoverImageOrLoader = () => {
        return state.selectedImage.path ? <LazyLoadImage
            src={S3_BUCKET + state.selectedImage.path} // use normal <img> attributes as props
            effect="blur"
            className="FundraiserCoverImage"
            onError={rollBackToDefaultCoverOnError}
        /> : <Stack className="FundraiserImageLoader"><img src={LoaderGif} /></Stack>
    }

    useNonInitialEffect(() => {

        if (defaultImages.length && !editView && !state.selectedImage.path)
            handleChange({ target: { name: "selectedImage", value: { ...state.selectedImage, path: defaultImages[0].path } } })

    }, [defaultImages]);

    useNonInitialEffect(() => {
        handleChange({
            target: {
                name: 'missionStatement',
                value: missionStatement
            }
        })
    }, [missionStatement])

    useNonInitialEffect(() => {
        setMissionStatement({
            ...state.missionStatement
        })
    }, [state.missionStatement.story])


    useNonInitialEffect(() => {
        if (preSelectedCampaign && !state.campaign) {
            const campaign = campaigns.find(campaign => campaign.id == preSelectedCampaign);
            setState({
                ...state,
                campaign: preSelectedCampaign,
                selectedImage: {...state.selectedImage, path: campaign?.fundraiser_default_image ? campaign.fundraiser_default_image : state.selectedImagePath},
                missionStatement: campaign?.fundraiser_default_story ? campaign.fundraiser_default_story : state.missionStatement
            });
        }
    }, [campaigns.length])

    useEffect(() => {
        if (!campaigns.length) dispatch(fundRaiserPageAction.getListOfCampaigns());
        if (!occasions.length) dispatch(fundRaiserPageAction.getListOfOccasions());
        if (!defaultImages.length) dispatch(fundRaiserPageAction.getFundRaiserPageDefaultImages());
    }, []);

    return <Stack gap={3} className="TellaStoryTab">
        <Stack direction={{ xs: 'column', md: 'row' }} className="CampaignOccasiong" justifyContent="space-between" gap={2}>
            <Dropdown
                name="campaign"
                label="Are you fundraising for a TCF Campaign?"
                margin="dense"
                value={state.campaign}
                onChange={handleChange}
                options={campaigns}
                fullWidth
                autoComplete />

            <Dropdown
                name="occasion"
                label="Are you fundraising for an Occasion?"
                margin="dense"
                value={state.occasion}
                onChange={handleChange}
                options={occasions}
                autoComplete
                fullWidth
            />
        </Stack>

        {
            state.selectedImage.default ? mapCoverImageOrLoader()
                :
                <React.Fragment>
                    <Cropper
                        image={state.selectedImage.path}
                        crop={crop}
                        zoom={zoom}
                        aspect={16 / 9}
                        onCropChange={setCrop}
                        onCropComplete={onCropComplete}
                        onZoomChange={setZoom}
                        showGrid={false}
                    />
                    <Slider
                        value={zoom}
                        min={1}
                        max={3}
                        step={0.1}
                        aria-labelledby="Zoom"
                        onChange={(e, zoom) => setZoom(zoom)}
                    />
                </React.Fragment>
        }

        <Stack flexDirection={'row'} gap={3} className="FundraiserCoverButtons">
            <MixPanelTrackedLink label="Upload your own" context="Upload on create/update fundraiser" type="button" color="inherit"
                onClick={() => imageUploadRef.current.click()}
            >
                <Typography variant="bold">Upload <UploadIcon /><Typography>your own</Typography></Typography>
            </MixPanelTrackedLink>

            <MixPanelTrackedLink label="Upload your own" context="Upload on create/update fundraiser" type="button" color="inherit"
                onClick={() => setOpenDefaultImagesModal(true)}
            >
                <Typography variant="bold">Choose <SquareStackIcon /><Typography>one of ours</Typography></Typography>
            </MixPanelTrackedLink>
        </Stack>

        <Box className="YourStoryBox">
            <Typography variant="bold" gutterBottom>Your Story</Typography>
            <SunEditor setOptions={{
                height: 200, buttonList: [] // Other option
            }}
                // onPaste={props.handlePaste}
                name="missionStatement"
                ref={missionStatementRef}
                onChange={onEditorChange}
                setContents={missionStatement.story}
            />
            <Typography variant="caption" color="error">{validationErrors?.missionStatement}</Typography>
        </Box>

        <ChooseImage
            showModal={openDefaultImagesModal}
            closeModal={() => setOpenDefaultImagesModal(false)}
            defaultImages={defaultImages}
            updateImage={(path) => { handleChange({ target: { name: "selectedImage", value: { ...state.selectedImage, path } } }); setOpenDefaultImagesModal(false) }}
        />
        <UploadImage handleUpload={uploadImage} ref={imageUploadRef} />
    </Stack>
}